import { pendingBattleId } from './economyClient';
import { economyFetch as fetch } from './economyClient';
/**
 * コレクション管理フック
 *
 * サーバー側のコレクションAPIと連携し、
 * パック開封・カード売却・データ同期を行う
 *
 * 使い方:
 * const {
 *   collection, gold, loading, error,
 *   openPack, sellCard, refresh
 * } = useCollection();
 */

import { useState, useEffect, useCallback } from 'react';

// ========================================
// Supabase設定（直接REST API使用）
// supabase.functions.invoke() はハングする問題があるため
// ========================================

const SUPABASE_URL = 'https://vikqkqwklbdjfhkydzpw.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZpa3FrcXdrbGJkamZoa3lkenB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwNjYzODcsImV4cCI6MjA4MTY0MjM4N30.PPIf98mu1HgR95CaymSJiV9E5hnlsMJuQeXw2MNqKsw';
const AUTH_STORAGE_KEY = 'sb-vikqkqwklbdjfhkydzpw-auth-token';
const API_TIMEOUT_MS = 30000;

function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.access_token || null;
    }
  } catch (err) {
    console.error('[useCollection] Failed to get access token:', err);
  }
  return null;
}

function withTimeout<T>(promise: Promise<T>, ms: number, operation: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error(`${operation}: タイムアウト（${ms}ms）`));
    }, ms);

    promise
      .then((result) => {
        clearTimeout(timeout);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeout);
        reject(error);
      });
  });
}

// ========================================
// 型定義
// ========================================

export interface CollectionCard {
  cardId: string;
  rarity: string;
  quantity: number;
}

export interface DrawnCard {
  cardId: string;
  rarity: string;
  isNew?: boolean;
}

export interface CollectionStats {
  totalBattles: number;
  wins: number;
  losses: number;
  packsOpened: number;
  totalGoldEarned: number;
  totalGoldSpent: number;
}

export interface CollectionState {
  gold: number;
  unopenedPacks: number;
  unopenedPacksByType: Record<string, number>;
  collection: CollectionCard[];
  decks: unknown[];
  stats: CollectionStats;
  loading: boolean;
  error: string | null;
  lastSync: Date | null;
}

export type PackType = 'normal' | 'fire' | 'water' | 'light' | 'dark' | 'primitive' | 'future' | 'royal';

export interface OpenPackResult {
  success: boolean;
  error?: string;
  cards?: DrawnCard[];
  goldSpent: number;
  newGoldBalance: number;
  packsOpened: number;
}

export interface SellCardResult {
  success: boolean;
  error?: string;
  goldEarned: number;
  marketPrice: number;
  newGoldBalance: number;
  remainingCount: number;
}

export interface BattleRewardResult {
  success: boolean;
  error?: string;
  goldReward: number;
  packReward: number;
  isWin: boolean;
  newGoldBalance: number;
  newUnopenedPacks: number;
}

export interface UseCollectionReturn extends CollectionState {
  /** コレクションデータを再取得 */
  refresh: () => Promise<void>;

  /** パックを開封 */
  openPack: (packType: PackType, count?: number, useUnopened?: boolean) => Promise<OpenPackResult>;

  /** カードを売却 */
  sellCard: (cardId: string, rarity: string, quantity: number) => Promise<SellCardResult>;

  /** デュエル報酬を付与（サーバー保存） */
  awardBattleReward: (isWin: boolean) => Promise<BattleRewardResult>;

  /** 特定カードの所持数を取得 */
  getCardCount: (cardId: string, rarity?: string) => number;

  /** サーバーと同期済みか */
  isSynced: boolean;
}

// ========================================
// ローカルストレージキャッシュ
// ========================================

const CACHE_KEY = 'magisp_collection_cache';

function saveToCache(data: Partial<CollectionState>): void {
  try {
    const cache = {
      ...data,
      cachedAt: new Date().toISOString(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save collection cache:', e);
  }
}

function loadFromCache(): Partial<CollectionState> | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data = JSON.parse(cached);

    // 1時間以上古いキャッシュは無視
    const cachedAt = new Date(data.cachedAt);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    if (cachedAt < hourAgo) {
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
}

// ========================================
// フック本体
// ========================================

export function useCollection(): UseCollectionReturn {
  const [state, setState] = useState<CollectionState>(() => {
    // 初期値（キャッシュがあれば使用）
    const cached = loadFromCache();
    return {
      gold: cached?.gold ?? 10000,
      unopenedPacks: cached?.unopenedPacks ?? 0,
      unopenedPacksByType: cached?.unopenedPacksByType ?? {
        normal: 0, fire: 0, water: 0, light: 0, dark: 0, primitive: 0, future: 0, royal: 0
      },
      collection: cached?.collection ?? [],
      decks: cached?.decks ?? [],
      stats: cached?.stats ?? {
        totalBattles: 0, wins: 0, losses: 0,
        packsOpened: 0, totalGoldEarned: 0, totalGoldSpent: 0,
      },
      loading: true,
      error: null,
      lastSync: cached?.lastSync ? new Date(cached.lastSync) : null,
    };
  });

  const [isSynced, setIsSynced] = useState(false);

  // ========================================
  // サーバーからデータ取得
  // ========================================

  const fetchCollection = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('認証が必要です');
      }

      const response = await withTimeout(
        fetch(`${SUPABASE_URL}/functions/v1/collection`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
        }),
        API_TIMEOUT_MS,
        'コレクション取得'
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data?.success) {
        const newState = {
          gold: data.gold,
          unopenedPacks: data.unopened_packs,
          unopenedPacksByType: data.unopened_packs_by_type,
          collection: (data.collection || []).map((c: { card_id: string; rarity: string; quantity: number }) => ({
            cardId: c.card_id,
            rarity: c.rarity,
            quantity: c.quantity,
          })),
          decks: data.decks || [],
          stats: data.stats || {},
          loading: false,
          error: null,
          lastSync: new Date(),
        };

        setState(newState);
        saveToCache(newState);
        setIsSynced(true);
      } else {
        throw new Error(data?.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Failed to fetch collection:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch collection',
      }));
      setIsSynced(false);
    }
  }, []);

  // 初回読み込み
  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  // ========================================
  // パック開封
  // ========================================

  const openPack = useCallback(async (
    packType: PackType,
    count = 1,
    useUnopened = false
  ): Promise<OpenPackResult> => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('認証が必要です');
      }

      const response = await withTimeout(
        fetch(`${SUPABASE_URL}/functions/v1/collection/pack/open`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            pack_type: packType,
            count,
            use_unopened: useUnopened,
          }),
        }),
        API_TIMEOUT_MS,
        'パック開封'
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data?.success) {
        return {
          success: false,
          error: data?.error || 'Failed to open pack',
          goldSpent: 0,
          newGoldBalance: state.gold,
          packsOpened: 0,
        };
      }

      // ローカル状態更新
      const newCards = data.cards || [];
      setState(prev => {
        // コレクションにカード追加
        const updatedCollection = [...prev.collection];
        for (const card of newCards) {
          const existing = updatedCollection.find(
            c => c.cardId === card.cardId && c.rarity === card.rarity
          );
          if (existing) {
            existing.quantity += 1;
          } else {
            updatedCollection.push({
              cardId: card.cardId,
              rarity: card.rarity,
              quantity: 1,
            });
          }
        }

        const newState = {
          ...prev,
          gold: data.new_gold_balance,
          collection: updatedCollection,
          stats: {
            ...prev.stats,
            packsOpened: (prev.stats.packsOpened || 0) + count,
            totalGoldSpent: (prev.stats.totalGoldSpent || 0) + data.gold_spent,
          },
        };

        saveToCache(newState);
        return newState;
      });

      return {
        success: true,
        cards: newCards,
        goldSpent: data.gold_spent,
        newGoldBalance: data.new_gold_balance,
        packsOpened: data.packs_opened,
      };
    } catch (err) {
      console.error('Failed to open pack:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to open pack',
        goldSpent: 0,
        newGoldBalance: state.gold,
        packsOpened: 0,
      };
    }
  }, [state.gold]);

  // ========================================
  // カード売却
  // ========================================

  const sellCard = useCallback(async (
    cardId: string,
    rarity: string,
    quantity: number
  ): Promise<SellCardResult> => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('認証が必要です');
      }

      const response = await withTimeout(
        fetch(`${SUPABASE_URL}/functions/v1/collection/sell`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            card_id: cardId,
            rarity,
            quantity,
          }),
        }),
        API_TIMEOUT_MS,
        'カード売却'
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data?.success) {
        return {
          success: false,
          error: data?.error || 'Failed to sell card',
          goldEarned: 0,
          marketPrice: 0,
          newGoldBalance: state.gold,
          remainingCount: 0,
        };
      }

      // ローカル状態更新
      setState(prev => {
        const updatedCollection = prev.collection.map(c => {
          if (c.cardId === cardId && c.rarity === rarity) {
            return { ...c, quantity: data.remaining_count };
          }
          return c;
        }).filter(c => c.quantity > 0);

        const newState = {
          ...prev,
          gold: data.new_gold_balance,
          collection: updatedCollection,
          stats: {
            ...prev.stats,
            totalGoldEarned: (prev.stats.totalGoldEarned || 0) + data.gold_earned,
          },
        };

        saveToCache(newState);
        return newState;
      });

      return {
        success: true,
        goldEarned: data.gold_earned,
        marketPrice: data.market_price,
        newGoldBalance: data.new_gold_balance,
        remainingCount: data.remaining_count,
      };
    } catch (err) {
      console.error('Failed to sell card:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to sell card',
        goldEarned: 0,
        marketPrice: 0,
        newGoldBalance: state.gold,
        remainingCount: 0,
      };
    }
  }, [state.gold]);

  // ========================================
  // デュエル報酬付与
  // ========================================

  const awardBattleReward = useCallback(async (
    isWin: boolean
  ): Promise<BattleRewardResult> => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        // 未認証の場合はスキップ（ローカルのみで処理される）
        console.log('[useCollection] Not authenticated, skipping server battle reward');
        return {
          success: false,
          error: 'Not authenticated',
          goldReward: 0,
          packReward: 0,
          isWin,
          newGoldBalance: state.gold,
          newUnopenedPacks: state.unopenedPacks,
        };
      }

      const response = await withTimeout(
        fetch(`${SUPABASE_URL}/functions/v1/collection/battle-reward`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            is_win: isWin,
          battle_id: pendingBattleId('normal'),
          }),
        }),
        API_TIMEOUT_MS,
        'デュエル報酬付与'
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data?.success) {
        return {
          success: false,
          error: data?.error || 'Failed to award battle reward',
          goldReward: 0,
          packReward: 0,
          isWin,
          newGoldBalance: state.gold,
          newUnopenedPacks: state.unopenedPacks,
        };
      }

      // ローカル状態更新
      setState(prev => {
        const newState = {
          ...prev,
          gold: data.new_gold_balance,
          unopenedPacks: data.new_unopened_packs,
          unopenedPacksByType: {
            ...prev.unopenedPacksByType,
            normal: (prev.unopenedPacksByType.normal || 0) + (data.pack_reward || 0),
          },
          stats: {
            ...prev.stats,
            totalBattles: (prev.stats.totalBattles || 0) + 1,
            wins: isWin ? (prev.stats.wins || 0) + 1 : (prev.stats.wins || 0),
            losses: !isWin ? (prev.stats.losses || 0) + 1 : (prev.stats.losses || 0),
            totalGoldEarned: (prev.stats.totalGoldEarned || 0) + data.gold_reward,
          },
        };

        saveToCache(newState);
        return newState;
      });

      console.log('[useCollection] Battle reward saved to server:', data);

      return {
        success: true,
        goldReward: data.gold_reward,
        packReward: data.pack_reward,
        isWin: data.is_win,
        newGoldBalance: data.new_gold_balance,
        newUnopenedPacks: data.new_unopened_packs,
      };
    } catch (err) {
      console.error('Failed to award battle reward:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to award battle reward',
        goldReward: 0,
        packReward: 0,
        isWin,
        newGoldBalance: state.gold,
        newUnopenedPacks: state.unopenedPacks,
      };
    }
  }, [state.gold, state.unopenedPacks]);

  // ========================================
  // ユーティリティ
  // ========================================

  const getCardCount = useCallback((cardId: string, rarity?: string): number => {
    if (rarity) {
      const card = state.collection.find(c => c.cardId === cardId && c.rarity === rarity);
      return card?.quantity || 0;
    }

    // レアリティ指定なしの場合は全レアリティの合計
    return state.collection
      .filter(c => c.cardId === cardId)
      .reduce((sum, c) => sum + c.quantity, 0);
  }, [state.collection]);

  // ========================================
  // 戻り値
  // ========================================

  return {
    gold: state.gold,
    unopenedPacks: state.unopenedPacks,
    unopenedPacksByType: state.unopenedPacksByType,
    collection: state.collection,
    decks: state.decks,
    stats: state.stats,
    loading: state.loading,
    error: state.error,
    lastSync: state.lastSync,
    isSynced,
    refresh: fetchCollection,
    openPack,
    sellCard,
    awardBattleReward,
    getCardCount,
  };
}

export default useCollection;

// ========================================
// 独立関数（フック外から呼び出し可能）
// ========================================

/**
 * デュエル報酬をサーバーに保存（フック外から呼び出し可能）
 * ログイン済みの場合のみサーバーに保存
 */
export async function saveBattleRewardToServer(isWin: boolean): Promise<BattleRewardResult> {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      // 未認証の場合はスキップ
      console.log('[saveBattleRewardToServer] Not authenticated, skipping');
      return {
        success: false,
        error: 'Not authenticated',
        goldReward: 0,
        packReward: 0,
        isWin,
        newGoldBalance: 0,
        newUnopenedPacks: 0,
      };
    }

    const response = await withTimeout(
      fetch(`${SUPABASE_URL}/functions/v1/collection/battle-reward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          is_win: isWin,
          battle_id: pendingBattleId('normal'),
        }),
      }),
      API_TIMEOUT_MS,
      'デュエル報酬付与'
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    if (!data?.success) {
      return {
        success: false,
        error: data?.error || 'Failed to award battle reward',
        goldReward: 0,
        packReward: 0,
        isWin,
        newGoldBalance: 0,
        newUnopenedPacks: 0,
      };
    }

    console.log('[saveBattleRewardToServer] Battle reward saved to server:', data);

    return {
      success: true,
      goldReward: data.gold_reward,
      packReward: data.pack_reward,
      isWin: data.is_win,
      newGoldBalance: data.new_gold_balance,
      newUnopenedPacks: data.new_unopened_packs,
    };
  } catch (err) {
    console.error('[saveBattleRewardToServer] Failed:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Failed to award battle reward',
      goldReward: 0,
      packReward: 0,
      isWin,
      newGoldBalance: 0,
      newUnopenedPacks: 0,
    };
  }
}
